// src/views/admin/WorkOrderPage.js
// Enhanced Work Order Management Page with AI Dispatcher, Helpdesk, SLA, & Live WebSocket Integrations
// + Deep Linking from Global Search + Performance optimizations (memoization, stable callbacks)
//
// --- UPDATE (v3 — sorting, live, AI, linking):
//   SORTING
//   • FIX: handleSort called setSortDirection inside the setSortField updater. React StrictMode runs
//     updaters twice, so clicking the same header flipped the direction twice (= no change). Sort is
//     now one state object updated by a pure function.
//   • FIX: sorting only reordered the 20 rows of the current page. sort_by / sort_dir / search are
//     now sent to fetchWorkOrders (server order across all pages) and the page is still sorted locally
//     so the view is always consistent. New sortable "AI Urgency" column (client-side triage score).
//   • Header arrows: ▲ asc / ▼ desc with aria-sort (was a trend icon for asc).
//   LIVE
//   • WebSocket subscribed ONCE (was re-subscribed on every page change); bursts of events are
//     coalesced into one silent reload (600 ms), full work-order payloads are patched in place
//     instantly, deletes removed, and reloads pause while the tab is hidden. Also listens to the
//     work_order_created / _updated / _completed / _assigned events other FlowSense pages emit.
//   • Cross-page bus (utils/flowsenseBus): every change made here is published so Public Utilities,
//     Road Works and other tabs refresh; asset changes elsewhere reload this list.
//   • Stale-response guard: a slow older request can no longer overwrite newer results.
//   ACTIONS
//   • Header "Export" downloaded nothing (toast only) — now exports the current view as CSV
//     (properly quoted; bulk export was not quoted either).
//   • Kanban drop never worked for numeric ids (string vs number); now optimistic with rollback, and
//     dropping an unassigned ticket on "Assigned" opens the Assign dialog.
//   • AI insight actions 'optimize', 'navigate', 'open-asset' and other filter payloads now do
//     something (were silently ignored). Every button has a handler.
//   • Numeric ids crashed search (`id.toLowerCase`) and the table (`id.substring`).
//   AI
//   • AI dispatch assistant bar (counts, one-click filters, accept all suggestions, do-these-first).
//   • AI urgency badge per row / kanban card with reasons; (?) explainers on SLA, Source, Urgency.
//   • Linked asset / road shown under each title; the View modal gets the AI helper + actions.
//   • New client filters from the AI bar: stagnant, not linked to an asset, closed without notes.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'; // --- UPDATE: + useNavigate, useLocation

// Chakra imports
import {
  Box, Heading, Button, Flex, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Spinner, Alert, AlertIcon, AlertTitle, AlertDescription, useColorModeValue,
  Tag, HStack, IconButton, Icon, useToast, useDisclosure,
  SimpleGrid, Divider, Text, Input, Select, InputGroup, InputLeftElement,
  Badge, Tooltip, Skeleton, VStack, Fade, ScaleFade, useBreakpointValue, 
  NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Stat, StatLabel, StatNumber, StatHelpText, Progress, Collapse,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Switch, FormControl, FormLabel, Code, Avatar,
} from '@chakra-ui/react';

import { keyframes } from '@emotion/react';

// Icons
import {
  MdAdd, MdEdit, MdDelete, MdVisibility, MdList, MdHourglassEmpty,
  MdSync, MdDone, MdNavigateBefore, MdNavigateNext, MdPersonAdd,
  MdSearch, MdFilterList, MdRefresh, MdDownload,
  MdCalendarToday, MdPriorityHigh, MdFirstPage, MdLastPage,
  MdTrendingUp, MdWarning, MdCheckCircle, MdSettings, MdInsights,
  MdAutoAwesome, MdLightbulb, MdSpeed, MdTimeline,
  MdAssignment, MdViewColumn, MdSmartToy, MdSensors, MdThumbUp,
  MdHeadsetMic, MdOutlineMap, MdPersonOff,
  MdWifi, MdWifiOff, MdSignalWifi4Bar
} from 'react-icons/md';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
// --- UPDATE: AI helper + cross-page bus
import { WorkOrderAiActionBar, WoUrgencyBadge, WoHint, WoTypeBadge } from '../../components/work_orders/WorkOrderAiHelper';
// --- UPDATE: work order types / domains + create-from-anywhere drafts
import {
  WORK_ORDER_TYPES, WORK_ORDER_DOMAINS, TYPE_KEYS, DOMAIN_KEYS, inferType, inferDomain, buildDraft, draftFromSearchParams,
} from '../../components/work_orders/workOrderCatalog';
// --- END UPDATE
import { getWorkOrderAnalysis, getLinkedEntities, isStagnant, FLOWSENSE_ROUTES } from '../../components/work_orders/workOrderAiGuide'; // --- UPDATE: + FLOWSENSE_ROUTES
import { publish as publishBus, subscribe as subscribeBus, BUS_EVENTS } from '../../utils/flowsenseBus';
// --- END UPDATE

// Import components
import CreateWorkOrderForm from '../../components/work_orders/CreateWorkOrderForm';
import EditWorkOrderForm from '../../components/work_orders/EditWorkOrderForm';
import ViewWorkOrderModal from '../../components/work_orders/ViewWorkOrderModal';
import AssignWorkOrderModal from '../../components/work_orders/AssignWorkOrderModal';
import RecordNotFoundModal from '../../components/common/RecordNotFoundModal';
import WorkOrderWatchdogPanel from '../../components/work_orders/WorkOrderWatchdogPanel';

// Import utilities
import { getUserInfo } from '../../utils/auth';
import { formatDate } from '../../utils/date';
import { getWorkOrderStatusColorScheme } from '../../utils/ui';

// Import services
import * as WorkOrderService from '../../services/workOrderService';
import webSocketService from '../../services/websocketService';

// =========================================================================
// ENTERPRISE FIX: Import from the Global AI Insights Context
// =========================================================================
import { useAIInsightsData, useInsightsRefresh } from '../../contexts/AIInsightsContext';

// Constants
const ALLOWED_STATUS = ['pending', 'in_progress', 'completed', 'cancelled', 'on_hold', 'open', 'assigned', 'closed', 'rejected'];
const ALLOWED_PRIORITY = ['low', 'medium', 'high', 'critical'];

// Kanban Column Configuration
const KANBAN_COLUMNS = [
  { id: 'pending', label: 'Pending / Open', statuses: ['pending', 'open'], color: 'yellow', icon: MdHourglassEmpty, targetStatus: 'pending' },
  { id: 'assigned', label: 'Assigned', statuses: ['assigned', 'scheduled', 'dispatched'], color: 'cyan', icon: MdAssignment, targetStatus: 'assigned' },
  { id: 'in_progress', label: 'In Progress', statuses: ['in_progress'], color: 'purple', icon: MdSync, targetStatus: 'in_progress' },
  { id: 'completed', label: 'Completed', statuses: ['completed', 'closed'], color: 'green', icon: MdDone, targetStatus: 'completed' }
];

const SORT_DATE_FIELDS = new Set(['due_date', 'created_at', 'updated_at', 'sla_resolution_deadline']);
// --- UPDATE: fields the API can order by (ai_urgency is computed in the browser)
const SERVER_SORT_FIELDS = new Set(['id', 'title', 'status', 'priority', 'sla_status', 'due_date', 'created_at', 'updated_at', 'assigned_user_username']);
const DESC_FIRST_FIELDS = new Set(['priority', 'sla_status', 'ai_urgency', 'created_at', 'updated_at']);
const LIVE_RELOAD_COALESCE_MS = 600;
const SEARCH_DEBOUNCE_MS = 350;
const WO_LIVE_EVENTS = ['work_order_events', 'work_order_created', 'work_order_updated', 'work_order_completed', 'work_order_assigned', 'work_order_deleted'];
const csvCell = (v) => {
  const t = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};
const downloadCsv = (rows, name) => {
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const WO_CSV_HEADERS = ['ID', 'Title', 'Status', 'Priority', 'SLA Status', 'SLA Deadline', 'Due Date', 'Assigned To', 'AI Suggested', 'AI Confidence', 'Source', 'Linked Asset', 'AI Urgency', 'Created'];
const woCsvRow = (wo) => {
  const a = getWorkOrderAnalysis(wo);
  return [wo.id, wo.title, wo.status, wo.priority, wo.sla_status || 'no_sla', wo.sla_resolution_deadline || '', formatDate(wo.due_date),
    wo.assigned_user_username || 'Unassigned', wo.suggested_technician_name || '', wo.assignment_confidence_score ?? '', wo.source || 'manual',
    a.links[0]?.label || '', a.closed ? '' : a.score, wo.created_at || ''];
};
// --- END UPDATE

const SLA_CONFIG_MAP = {
  compliant: { color: 'green', label: 'Compliant' },
  at_risk:   { color: 'orange', label: 'At Risk' },
  breached:  { color: 'red', label: 'Breached' },
  critical:  { color: 'orange', label: 'Critical' },
  met:       { color: 'green', label: 'Met' }
};

const PRIORITY_COLOR_MAP = { low: 'gray', medium: 'blue', high: 'orange', critical: 'red' };

const PRIORITY_WEIGHTS = { critical: 4, high: 3, medium: 2, low: 1 };
const SLA_WEIGHTS = { breached: 5, critical: 4, at_risk: 3, compliant: 2, met: 1, no_sla: 0 };

const INSIGHT_ICON_MAP = {
  MdWarning, MdPersonAdd, MdPriorityHigh, MdCheckCircle, MdTrendingUp,
  MdSpeed, MdTimeline, MdAutoAwesome
};

const pulseRing = keyframes`
  0%   { transform: scale(0.8); opacity: 1; }
  70%  { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50%        { opacity: 0.5; }
`;

const SourceBadge = React.memo(({ source }) => {
  switch (source) {
    case 'ai_prescriptive':
    case 'prediction':
      return (
        <Tooltip label="Predicted by ML Engine" hasArrow>
          <span><Tag size="sm" colorScheme="purple" variant="subtle"><Icon as={MdSmartToy} mr={1} />AI Pred</Tag></span>
        </Tooltip>
      );
    case 'ai_sensor_alert':
      return (
        <Tooltip label="Triggered by Sensor Alert" hasArrow>
          <span><Tag size="sm" colorScheme="orange" variant="subtle"><Icon as={MdSensors} mr={1} />IoT Alert</Tag></span>
        </Tooltip>
      );
    case 'helpdesk':
      return (
        <Tooltip label="Escalated from Helpdesk Ticket" hasArrow>
          <span><Tag size="sm" colorScheme="teal" variant="subtle"><Icon as={MdHeadsetMic} mr={1} />Helpdesk</Tag></span>
        </Tooltip>
      );
    default: 
      return (
        <Tooltip label="Manually Created" hasArrow>
          <span><Tag size="sm" colorScheme="gray" variant="subtle"><Icon as={MdAdd} mr={1} />Manual</Tag></span>
        </Tooltip>
      );
  }
});
SourceBadge.displayName = 'SourceBadge';

const SLAStatusBadge = React.memo(({ status }) => {
  if (!status || status === 'no_sla') return <Badge colorScheme="gray" fontSize="2xs">No SLA</Badge>;
  const config = SLA_CONFIG_MAP[status] || { color: 'gray', label: status.replace(/_/g, ' ') };
  return <Badge colorScheme={config.color} fontSize="2xs" variant="outline">{config.label}</Badge>;
});
SLAStatusBadge.displayName = 'SLAStatusBadge';

const PriorityBadge = React.memo(({ priority }) => {
  const colorScheme = PRIORITY_COLOR_MAP[priority] || 'gray';
  return <Badge colorScheme={colorScheme} fontSize="xs" px={2} py={1} borderRadius="md" fontWeight="semibold">{priority}</Badge>;
});
PriorityBadge.displayName = 'PriorityBadge';

// --- UPDATE: module-level sortable header (defined outside render so it never remounts)
const SortableHeader = React.memo(({ field, children, width, hint, sortField, sortDirection, onSort, color, hoverBg }) => {
  const isActive = sortField === field;
  return (
    <Th
      color={isActive ? 'blue.500' : color} fontSize="xs" textTransform="uppercase" cursor="pointer" width={width}
      onClick={() => onSort(field)} _hover={{ bg: hoverBg }} transition="background 0.2s"
      aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(field); } }}
      title={`Sort by ${typeof children === 'string' ? children : field}`}
    >
      <HStack spacing={1}>
        <Text>{children}</Text>
        {hint}
        <Icon as={isActive && sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon} boxSize={3} color="blue.500" opacity={isActive ? 1 : 0.25} />
      </HStack>
    </Th>
  );
});
SortableHeader.displayName = 'SortableHeader';
// --- END UPDATE

function WorkOrderPage() {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // --- UPDATE: after the first successful load, re-sorts / searches / page changes keep the table mounted
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // --- END UPDATE
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isOptimizingRoutes, setIsOptimizingRoutes] = useState(false);

  const [workOrderStats, setWorkOrderStats] = useState({
    total: 0, pending: 0, in_progress: 0, completed: 0, high_priority: 0,
    overdue: 0, unassigned: 0, ai_suggested: 0, ai_predicted: 0,
    sensor_alerts: 0, avg_confidence: 0
  });
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); 
  const [suggestionFilter, setSuggestionFilter] = useState('all'); 
  const [slaFilter, setSlaFilter] = useState('all'); 
  const [unassignedCriticalFilter, setUnassignedCriticalFilter] = useState(false); 
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  
  const [showInsights, setShowInsights] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [compactView, setCompactView] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // --- UPDATE: one state object → pure toggle (StrictMode-safe)
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const sortField = sort.field;
  const sortDirection = sort.dir;
  const setSortField = useCallback((field) => setSort(prev => ({ ...prev, field })), []);
  const setSortDirection = useCallback((dir) => setSort(prev => ({ ...prev, dir })), []);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stagnantFilter, setStagnantFilter] = useState(false);
  const [noAssetFilter, setNoAssetFilter] = useState(false);
  const [noNotesFilter, setNoNotesFilter] = useState(false);
  // --- UPDATE: type / domain filters + create draft (asset, sensor, well, road, inspection …)
  const [typeFilter, setTypeFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [createDraft, setCreateDraft] = useState(null);
  const location = useLocation();
  // --- END UPDATE
  const [isBulkAccepting, setIsBulkAccepting] = useState(false);
  const navigate = useNavigate();
  const loadReqIdRef = useRef(0);
  // --- END UPDATE
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  
  const [isAcceptingSuggestion, setIsAcceptingSuggestion] = useState(null); 
  const [isProcessingAI, setIsProcessingAI] = useState(false);

  const [wsStatus, setWsStatus] = useState('connecting');
  const [lastWsEvent, setLastWsEvent] = useState(null);
  const [wsEventCount, setWsEventCount] = useState(0);
  
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();
  const { isOpen: isAssignModalOpen, onOpen: onAssignModalOpen, onClose: onAssignModalClose } = useDisclosure();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();

  const { isOpen: isNotFoundOpen, onOpen: onNotFoundOpen, onClose: onNotFoundClose } = useDisclosure();
  const [notFoundQuery, setNotFoundQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [viewingWorkOrder, setViewingWorkOrder] = useState(null);
  const [assigningWorkOrder, setAssigningWorkOrder] = useState(null);
  
  const toast = useToast();

  // ============================================================================
  // THEME COLORS
  // ============================================================================
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const tableHeaderColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const trHoverBg = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const insightsBg = useColorModeValue('blue.50', 'blue.900');
  const insightsBorder = useColorModeValue('blue.200', 'blue.700');
  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningBorder = useColorModeValue('orange.200', 'orange.700');
  const successBg = useColorModeValue('green.50', 'green.900');
  const successBorder = useColorModeValue('green.200', 'green.700');
  
  const blueIconBg = useColorModeValue('blue.50', 'blue.900');
  const yellowIconBg = useColorModeValue('yellow.50', 'yellow.900');
  const purpleIconBg = useColorModeValue('purple.50', 'purple.900');
  const greenIconBg = useColorModeValue('green.50', 'green.900');
  
  const bulkActionsBg = useColorModeValue('blue.50', 'blue.900');
  const selectedRowBg = useColorModeValue('blue.50', 'blue.900');
  const sortableHeaderHoverBg = useColorModeValue('gray.100', 'gray.600');
  const kanbanColumnBg = useColorModeValue('gray.100', 'gray.900');
  
  const aiSuggestionBg = useColorModeValue('purple.50', 'purple.900');

  const wsConnectedBg    = useColorModeValue('green.50',  'green.900');
  const wsConnectedBorder= useColorModeValue('green.200', 'green.700');
  const wsDisconnectedBg = useColorModeValue('red.50',    'red.900');
  const wsDisconnectedBorder = useColorModeValue('red.200', 'red.700');
  const wsConnectingBg   = useColorModeValue('yellow.50', 'yellow.900');
  const wsConnectingBorder = useColorModeValue('yellow.200','yellow.700');
  
  const isMobile = useBreakpointValue({ base: true, md: false });
  const tableSize = useBreakpointValue({ base: 'sm', md: 'md' });

  const iconBgMap = useMemo(() => ({
    blue: blueIconBg, yellow: yellowIconBg, purple: purpleIconBg, green: greenIconBg
  }), [blueIconBg, yellowIconBg, purpleIconBg, greenIconBg]);

  const insightBgMap = useMemo(() => ({
    warning: warningBg, info: insightsBg, success: successBg
  }), [warningBg, insightsBg, successBg]);

  const insightBorderMap = useMemo(() => ({
    warning: warningBorder, info: insightsBorder, success: successBorder
  }), [warningBorder, insightsBorder, successBorder]);

  // ============================================================================
  // GLOBAL AI INSIGHTS INTEGRATION (OMNIPRESENT LAYER)
  // ============================================================================

  const {
    insights: aiInsightsData,
    isLoading: isGeneratingInsights,
    error: insightsError,
    lastUpdated: insightsLastGenerated,
    exportAsJSON: exportAIInsights,
    setLocalWorkOrders
  } = useAIInsightsData();

  const { refresh: refreshInsights } = useInsightsRefresh();

  // Sync local work orders up to the Global Context so the AI brain can see them
  useEffect(() => {
    if (setLocalWorkOrders) {
        setLocalWorkOrders(workOrders);
    }
  }, [workOrders, setLocalWorkOrders]);

  const aiInsights = useMemo(() => {
    if (!aiInsightsData) return [];
    return aiInsightsData.map(insight => ({
      type: insight.type,
      // Handle case where icon is already a component or needs mapping
      icon: (typeof insight.icon === 'function' || typeof insight.icon === 'object') 
              ? insight.icon 
              : (INSIGHT_ICON_MAP[insight.icon] || MdLightbulb),
      title: insight.title,
      message: insight.message,
      action: insight.action,
      actionType: insight.actionType,
      actionPayload: insight.actionPayload,
    }));
  }, [aiInsightsData]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  
  const loadGlobalStats = useCallback(async () => {
    try {
      const res = await WorkOrderService.fetchWorkOrderGlobalStats();
      if (res && res.data) {
        setWorkOrderStats(res.data);
      }
    } catch (err) {
      console.error("[WorkOrderPage] Failed to fetch global stats:", err);
    }
  }, []);

  const loadWorkOrders = useCallback(async (page = 1, isSilent = false) => {
    // --- UPDATE: request-sequence guard (stale responses are dropped)
    const reqId = ++loadReqIdRef.current;
    // --- END UPDATE
    if (!isSilent) setIsLoading(true);
    setError('');

    try {
      const filters = { 
        page, 
        limit: viewMode === 'kanban' ? 100 : itemsPerPage, 
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        sla_status: slaFilter !== 'all' ? slaFilter : undefined,
        // --- UPDATE: server-side order + search so sorting spans all pages
        sort_by: SERVER_SORT_FIELDS.has(sortField) ? sortField : undefined,
        sort_dir: SERVER_SORT_FIELDS.has(sortField) ? sortDirection : undefined,
        search: debouncedSearch || undefined,
        work_order_type: typeFilter !== 'all' ? typeFilter : undefined,
        domain: domainFilter !== 'all' ? domainFilter : undefined,
        // --- END UPDATE
      };

      const result = await WorkOrderService.fetchWorkOrders(filters);
      if (reqId !== loadReqIdRef.current) return; // --- UPDATE: newer request in flight
      
      setWorkOrders(result?.data || []);
      setCurrentPage(result?.pagination?.currentPage || 1);
      setTotalPages(result?.pagination?.totalPages || 1);
      setTotalItems(result?.pagination?.totalItems || 0);
      setLastRefresh(new Date());
      setHasLoadedOnce(true); // --- UPDATE

    } catch (err) {
      if (reqId !== loadReqIdRef.current) return; // --- UPDATE
      if (!isSilent) setError(err.message);
      if (!isSilent) toast({
        title: 'Load Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      if (!isSilent && reqId === loadReqIdRef.current) setIsLoading(false);
    }
  }, [toast, itemsPerPage, statusFilter, priorityFilter, sourceFilter, slaFilter, viewMode, sortField, sortDirection, debouncedSearch, typeFilter, domainFilter]);

  // --- UPDATE: stats once; list on every filter / sort / search change (was: stats refetched each time)
  useEffect(() => {
    setCurrentUser(getUserInfo());
    loadGlobalStats();
  }, [loadGlobalStats]);
  useEffect(() => {
    loadWorkOrders(1);
  }, [loadWorkOrders]);
  useEffect(() => {
    if (searchQuery === debouncedSearch) return undefined;
    const t = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery, debouncedSearch]);
  // --- END UPDATE

  // --- UPDATE: latest list for deep links (open instantly from the loaded page, then refresh from the API)
  const workOrdersRef = useRef(workOrders);
  workOrdersRef.current = workOrders;
  // --- END UPDATE
  const loadWorkOrdersRef = useRef(loadWorkOrders);
  const loadGlobalStatsRef = useRef(loadGlobalStats);

  useEffect(() => {
    loadWorkOrdersRef.current = loadWorkOrders;
    loadGlobalStatsRef.current = loadGlobalStats;
  }, [loadWorkOrders, loadGlobalStats]);

  // ============================================================================
  // DEEP LINKING INTERCEPTOR
  // ============================================================================
  // --- UPDATE: deep links from every FlowSense page (Road Works, Helpdesk, Phone AI, Utilities, insights, chat links)
  // Accepted:  ?search=<id> | ?id= | ?wo= | ?workOrder= | ?open=      (opens the View modal)
  //            state { openWorkOrderId | workOrderId | workOrder }      (navigate(...) without a query string)
  //            ?filter=overdue|sla_risk|breached|at_risk|stagnant|unassigned_critical|no_asset  ?status= ?source= ?priority=
  //            ?road_id= ?asset_id= ?road= ?asset=                       (searches the list for that record)
  // Runs on every navigation to the page (was mount-only, so a second link while on the page did nothing).
  const DEEP_LINK_KEYS = ['search', 'id', 'wo', 'workOrder', 'open', 'filter', 'status', 'source', 'priority', 'road_id', 'asset_id', 'road', 'asset'];
  const deepLinkSeqRef = useRef(0);
  const openWorkOrderById = useCallback(async (rawId, { fallbackSearch = true } = {}) => {
    const id = String(rawId || '').trim();
    if (!id) return false;
    const seq = ++deepLinkSeqRef.current;
    const cached = workOrdersRef.current?.find?.(wo => String(wo.id) === id);
    if (cached) { setViewingWorkOrder(cached); onViewModalOpen(); }
    try {
      const wo = await WorkOrderService.fetchWorkOrderById(id);
      if (seq !== deepLinkSeqRef.current) return true;
      if (wo && wo.id !== undefined && wo.id !== null) {
        setViewingWorkOrder(wo);
        if (!cached) onViewModalOpen();
        return true;
      }
      throw new Error('Not Found');
    } catch (error) {
      if (seq !== deepLinkSeqRef.current || cached) return !!cached;
      console.warn('[WorkOrderPage] Deep link failed, record not found:', error.message);
      if (fallbackSearch) setSearchQuery(id);
      setNotFoundQuery(id);
      onNotFoundOpen();
      return false;
    }
  }, [onViewModalOpen, onNotFoundOpen]);

  useEffect(() => {
    const state = location?.state || {};
    const pick = (...keys) => keys.map(k => searchParams.get(k)).find(v => v !== null && String(v).trim() !== '');
    const linkId = pick('search', 'id', 'wo', 'workOrder', 'open');
    const stateWo = state.workOrder && typeof state.workOrder === 'object' ? state.workOrder : null;
    const stateId = state.openWorkOrderId ?? state.workOrderId ?? stateWo?.id;
    const filter = pick('filter');
    const status = pick('status');
    const source = pick('source');
    const priority = pick('priority');
    const recordSearch = pick('road_id', 'asset_id', 'road', 'asset');

    if (!linkId && stateId === undefined && !filter && !status && !source && !priority && !recordSearch) return;

    if (filter || status || source || priority || recordSearch) clearFilters();
    if (filter === 'breached' || filter === 'overdue') setSlaFilter('breached');
    if (filter === 'at_risk' || filter === 'sla_risk') setSlaFilter('at_risk');
    if (filter === 'stagnant') setStagnantFilter(true);
    if (filter === 'unassigned_critical' || filter === 'unassigned') setUnassignedCriticalFilter(true);
    if (filter === 'no_asset') setNoAssetFilter(true);
    if (filter === 'no_notes') setNoNotesFilter(true);
    if (status && (ALLOWED_STATUS.includes(status) || status === 'on_hold')) setStatusFilter(status);
    if (source) setSourceFilter(source);
    if (priority) setPriorityFilter(priority);
    if (recordSearch) setSearchQuery(recordSearch);

    if (stateWo) { setViewingWorkOrder(stateWo); onViewModalOpen(); }
    if (linkId) { setSearchQuery(linkId); openWorkOrderById(linkId); }
    else if (stateId !== undefined && stateId !== null) openWorkOrderById(stateId, { fallbackSearch: true });

    // consume the link so a refresh / back does not reopen it
    let changed = false;
    DEEP_LINK_KEYS.forEach(k => { if (searchParams.has(k)) { searchParams.delete(k); changed = true; } });
    if (changed) setSearchParams(searchParams, { replace: true });
    if (stateId !== undefined || stateWo) navigate(location.pathname + (changed ? '' : location.search), { replace: true, state: { ...state, openWorkOrderId: undefined, workOrderId: undefined, workOrder: undefined } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search]);
  // --- END UPDATE

  // --- UPDATE: CREATE FROM ANYWHERE IN FLOWSENSE
  // navigate('/admin/workorders', { state: { woDraft } })   → opens the form pre-filled
  // navigate(..., { state: { autoTriage, ... } })           → Live Grid AI triage (form reads it)
  // /admin/workorders?create=1&domain=road&road_id=12&category=Line%20Striping
  // The user's role loads asynchronously, so the request is held until canCreate is known.
  const pendingCreateRef = useRef(undefined);
  useEffect(() => {
    const state = location?.state;
    const fromParams = draftFromSearchParams(searchParams);
    if (state?.woDraft) pendingCreateRef.current = state.woDraft;
    else if (state?.autoTriage) pendingCreateRef.current = null; // the form reads the Live Grid triage state itself
    else if (fromParams) {
      pendingCreateRef.current = fromParams;
      ['create', 'domain', 'work_order_type', 'category', 'priority', 'asset_id', 'sensor_id', 'well_id', 'road_id', 'parent_work_order_id', 'inspection_ref_id', 'inspection_source', 'title', 'location_description']
        .forEach(k => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // --- END UPDATE

  // ============================================================================
  // WEBSOCKET INTEGRATION FOR LIVE UPDATES
  // --- UPDATE: subscribe ONCE, coalesce reloads, patch payloads in place, pause when hidden
  // ============================================================================
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const liveTimerRef = useRef(null);
  const hiddenPendingRef = useRef(false);

  const scheduleLiveReload = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { hiddenPendingRef.current = true; return; }
    if (liveTimerRef.current) return;
    liveTimerRef.current = setTimeout(() => {
      liveTimerRef.current = null;
      loadWorkOrdersRef.current(currentPageRef.current, true);
      loadGlobalStatsRef.current();
    }, LIVE_RELOAD_COALESCE_MS);
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'hidden' && hiddenPendingRef.current) {
        hiddenPendingRef.current = false;
        scheduleLiveReload();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); clearTimeout(liveTimerRef.current); };
  }, [scheduleLiveReload]);

  useEffect(() => {
    setWsStatus('connecting');

    const handleConnect = () => setWsStatus('connected');
    const handleDisconnect = () => setWsStatus('disconnected');
    const handleReconnecting = () => setWsStatus('reconnecting');

    if (typeof webSocketService.on === 'function') {
      webSocketService.on('connect',      handleConnect);
      webSocketService.on('disconnect',   handleDisconnect);
      webSocketService.on('reconnecting', handleReconnecting);
    }

    webSocketService.connect();

    const markEvent = () => {
      setWsStatus('connected');
      setLastWsEvent(new Date());
      setWsEventCount(c => c + 1);
    };

    const handleMlEvents = (msg) => {
      if (msg?.event === 'ai_model_promoted') {
        toastRef.current({
            title: '🚀 AI Dispatch Model Upgraded',
            description: `The background neural network just autonomously promoted a new Champion model (v${msg.version}) with an accuracy score of ${msg.f1_score}%.`,
            status: 'success',
            duration: 8000,
            isClosable: true,
            position: 'top-right',
            variant: 'solid'
        });
        markEvent();
        loadGlobalStatsRef.current();
      }
    };

    // One handler for every work-order channel; `channel` tells which one fired
    const makeWorkOrderHandler = (channel) => (msg = {}) => {
      markEvent();
      const type = msg.type || channel.replace('work_order_', '');
      const payload = msg.data || msg.workOrder || msg.work_order || (msg.id != null && msg.title ? msg : null);

      if ((type === 'deleted' || channel === 'work_order_deleted') && (payload?.id ?? msg.id) != null) {
        const delId = String(payload?.id ?? msg.id);
        setWorkOrders(prev => prev.filter(wo => String(wo.id) !== delId));
      } else if (payload?.id != null && type !== 'created') {
        // In-place patch so the row changes before the reload lands
        setWorkOrders(prev => prev.map(wo => (String(wo.id) === String(payload.id) ? { ...wo, ...payload } : wo)));
      }
      scheduleLiveReload();

      if (type === 'created') {
        const critical = ['critical', 'high'].includes(String(payload?.priority || msg.priority).toLowerCase());
        toastRef.current({
          title: critical ? 'New high-priority ticket' : 'New Ticket Received',
          description: payload?.title || msg.title,
          status: critical ? 'warning' : 'info',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
          variant: 'left-accent'
        });
      }
    };

    webSocketService.subscribe('ml_events', handleMlEvents);
    const woHandlers = WO_LIVE_EVENTS.map(ch => [ch, makeWorkOrderHandler(ch)]);
    woHandlers.forEach(([ch, h]) => webSocketService.subscribe(ch, h));

    // Cross-page bus: assets changed elsewhere (e.g. failure reported) can create / change work orders
    const offBus = subscribeBus((type, payload, meta) => {
      if (type === BUS_EVENTS.ASSET_CHANGED || type === BUS_EVENTS.ROAD_CHANGED) scheduleLiveReload();
      if (type === BUS_EVENTS.WORK_ORDER_CHANGED && meta?.source === 'tab') scheduleLiveReload(); // another tab
    });

    const inferConnectedTimer = setTimeout(() => {
      setWsStatus(prev => prev === 'connecting' ? 'connected' : prev);
    }, 1500);

    return () => {
      clearTimeout(inferConnectedTimer);
      webSocketService.unsubscribe('ml_events', handleMlEvents);
      woHandlers.forEach(([ch, h]) => webSocketService.unsubscribe(ch, h));
      offBus();
      if (typeof webSocketService.off === 'function') {
        webSocketService.off('connect',      handleConnect);
        webSocketService.off('disconnect',   handleDisconnect);
        webSocketService.off('reconnecting', handleReconnecting);
      }
    };
  }, [scheduleLiveReload]);
  // --- END UPDATE

  // ============================================================================
  // BULLETPROOF CLIENT-SIDE FILTERING & SORTING
  // ============================================================================
  const displayedWorkOrders = useMemo(() => {
    let filtered = workOrders;
    
    // 1. Search Query Filter
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(wo => 
            wo.title?.toLowerCase().includes(query) ||
            String(wo.id ?? '').toLowerCase().includes(query) || // --- UPDATE: numeric-id safe
            wo.description?.toLowerCase().includes(query) ||
            wo.assigned_user_username?.toLowerCase().includes(query) ||
            wo.suggested_technician_name?.toLowerCase().includes(query)
        );
    }
    
    // 2. Status Filter
    if (statusFilter !== 'all') {
        filtered = filtered.filter(wo => wo.status === statusFilter);
    }

    // 3. Priority Filter
    if (priorityFilter !== 'all') {
        filtered = filtered.filter(wo => wo.priority === priorityFilter);
    }

    // 4. Source Filter
    if (sourceFilter !== 'all') {
        filtered = filtered.filter(wo => wo.source === sourceFilter);
    }

    // 5. SLA Filter
    if (slaFilter !== 'all') {
        if (slaFilter === 'no_sla') {
             filtered = filtered.filter(wo => !wo.sla_status || wo.sla_status === 'no_sla');
        } else {
             filtered = filtered.filter(wo => wo.sla_status === slaFilter);
        }
    }

    // 6. Assignment & AI Suggestion Filter
    if (suggestionFilter !== 'all') {
        if (suggestionFilter === 'has_suggestion') {
            filtered = filtered.filter(wo => wo.suggested_user_id && !wo.assigned_user_id);
        } else if (suggestionFilter === 'no_suggestion') {
            filtered = filtered.filter(wo => !wo.suggested_user_id);
        } else if (suggestionFilter === 'unassigned') {
            filtered = filtered.filter(wo => !wo.assigned_user_id);
        } else if (suggestionFilter === 'assigned') {
            filtered = filtered.filter(wo => !!wo.assigned_user_id);
        }
    }

    // 6.5. Unassigned Critical Filter (From Watchdog Panel)
    if (unassignedCriticalFilter) {
        filtered = filtered.filter(wo => !wo.assigned_user_id && ['high', 'critical'].includes(wo.priority));
    }
    
    // --- UPDATE: AI-bar filters
    if (stagnantFilter) filtered = filtered.filter(wo => isStagnant(wo));
    if (noAssetFilter) filtered = filtered.filter(wo => getLinkedEntities(wo).length === 0);
    if (noNotesFilter) filtered = filtered.filter(wo => ['completed', 'closed'].includes(wo.status) && !wo.resolution_notes);
    // type / domain (inferred for older rows so they still match)
    if (typeFilter !== 'all') filtered = filtered.filter(wo => inferType(wo) === typeFilter);
    if (domainFilter !== 'all') filtered = filtered.filter(wo => inferDomain(wo) === domainFilter);
    // --- END UPDATE

    // 7. Dynamic Type-Aware & Weighted Sorting
    return [...filtered].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        
        // --- UPDATE: AI urgency (computed) + numeric ids
        if (sortField === 'ai_urgency') {
            aVal = getWorkOrderAnalysis(a).score;
            bVal = getWorkOrderAnalysis(b).score;
        }
        else if (sortField === 'id' && Number.isFinite(Number(aVal)) && Number.isFinite(Number(bVal))) {
            aVal = Number(aVal);
            bVal = Number(bVal);
        }
        else
        // --- END UPDATE
        if (sortField === 'priority') {
            aVal = PRIORITY_WEIGHTS[aVal?.toLowerCase()] || 0;
            bVal = PRIORITY_WEIGHTS[bVal?.toLowerCase()] || 0;
        }
        else if (sortField === 'sla_status') {
            aVal = SLA_WEIGHTS[aVal?.toLowerCase()] || 0;
            bVal = SLA_WEIGHTS[bVal?.toLowerCase()] || 0;
        }
        else if (SORT_DATE_FIELDS.has(sortField)) {
            aVal = aVal ? new Date(aVal).getTime() : 0;
            bVal = bVal ? new Date(bVal).getTime() : 0;
        } 
        else {
            aVal = (aVal ?? '').toString().toLowerCase();
            bVal = (bVal ?? '').toString().toLowerCase();
        }
        
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });
  }, [workOrders, searchQuery, statusFilter, priorityFilter, sourceFilter, slaFilter, suggestionFilter, unassignedCriticalFilter, sortField, sortDirection, stagnantFilter, noAssetFilter, noNotesFilter, typeFilter, domainFilter]);

  
  const nowTime = useMemo(() => Date.now(), [displayedWorkOrders]);

  // --- UPDATE: tell the rest of FlowSense (other pages / tabs) a work order changed
  const announce = useCallback((action, wo, extra = {}) => {
    publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, {
      action, id: wo?.id ?? wo, status: extra.status ?? wo?.status,
      asset_id: wo?.asset_id ?? null, road_id: wo?.road_id ?? null, ...extra,
    });
  }, []);
  // Keep the open View modal in sync with live / reloaded data
  useEffect(() => {
    if (!viewingWorkOrder) return;
    const fresh = workOrders.find(wo => String(wo.id) === String(viewingWorkOrder.id));
    if (fresh && fresh !== viewingWorkOrder && JSON.stringify(fresh) !== JSON.stringify(viewingWorkOrder)) setViewingWorkOrder(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders]);
  // --- END UPDATE

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  const handleCreate = useCallback(() => { setCreateDraft(null); onCreateModalOpen(); }, [onCreateModalOpen]);
  // --- UPDATE: create with a draft (new inspection, linked inspection, follow-up from findings)
  const openCreateWithDraft = useCallback((draft) => { setCreateDraft(draft); onCreateModalOpen(); }, [onCreateModalOpen]);
  const handleCreateInspection = useCallback(() => openCreateWithDraft({ work_order_type: 'inspection', priority: 'medium' }), [openCreateWithDraft]);
  const handleCreateLinked = useCallback((kind, wo) => {
    if (!wo) return;
    onViewModalClose();
    openCreateWithDraft(buildDraft(kind === 'inspection' ? 'qa_inspection' : 'inspection', wo));
  }, [onViewModalClose, openCreateWithDraft]);
  // --- END UPDATE
  
  const handleEdit = useCallback((workOrder) => {
    setEditingWorkOrder(workOrder);
    onEditModalOpen();
  }, [onEditModalOpen]);
  
  const handleView = useCallback((workOrder) => {
    setViewingWorkOrder(workOrder);
    onViewModalOpen();
  }, [onViewModalOpen]);
  
  const handleAssign = useCallback((workOrder) => {
    setAssigningWorkOrder(workOrder);
    onAssignModalOpen();
  }, [onAssignModalOpen]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this work order?')) return;
    try {
      await WorkOrderService.deleteWorkOrder(id);
      announce('deleted', id); // --- UPDATE
      setSelectedRows(prev => { const n = new Set(prev); n.delete(id); return n; }); // --- UPDATE
      toast({ title: 'Work Order Deleted', status: 'success', duration: 3000, isClosable: true });
      loadWorkOrders(currentPage);
      loadGlobalStats();
    } catch (err) {
      toast({ title: 'Delete Failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
  }, [toast, loadWorkOrders, currentPage, loadGlobalStats, announce]);

  const handleAcceptSuggestion = useCallback(async (e, workOrder) => {
    if (e) e.stopPropagation(); 
    if (!workOrder.suggested_user_id) return;

    setIsAcceptingSuggestion(workOrder.id);
    try {
      await WorkOrderService.acceptSuggestion(workOrder.id);
      announce('assigned', workOrder); // --- UPDATE
      toast({
        title: 'AI Assignment Accepted',
        description: `Assigned to ${workOrder.suggested_technician_name || 'technician'}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      loadWorkOrders(currentPage);
      loadGlobalStats();
    } catch (err) {
      toast({ title: 'Assignment Failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsAcceptingSuggestion(null);
    }
  }, [toast, loadWorkOrders, currentPage, loadGlobalStats, announce]);

  const acceptSuggestion = useCallback(async (workOrderId) => {
    try {
      await WorkOrderService.acceptSuggestion(workOrderId);
      return { id: workOrderId, status: 'success' };
    } catch (error) {
      return { id: workOrderId, status: 'failed', error: error.message };
    }
  }, []);

  const handleRefreshInsights = useCallback(() => {
    refreshInsights();
    toast({ title: 'Insights Refreshed', status: 'success', duration: 2000 });
  }, [refreshInsights, toast]);

  const handleRefresh = useCallback(() => {
    loadWorkOrders(currentPage);
    loadGlobalStats();
    handleRefreshInsights();
  }, [loadWorkOrders, currentPage, loadGlobalStats, handleRefreshInsights]);
  
  const handlePageChange = useCallback((newPage) => {
    // --- UPDATE: NaN guard (typing in the page box) + clear selection on page change
    const p = Number(newPage);
    if (!Number.isFinite(p)) return;
    if (p >= 1 && p <= totalPages) {
      setSelectedRows(new Set());
      setSelectAll(false);
      loadWorkOrders(p);
    }
  }, [totalPages, loadWorkOrders]);

  const handleSuccess = useCallback((changed) => {
    loadWorkOrders(currentPage, true);
    loadGlobalStats();
    announce('updated', changed && typeof changed === 'object' ? changed : viewingWorkOrder || editingWorkOrder || assigningWorkOrder); // --- UPDATE
  }, [loadWorkOrders, currentPage, loadGlobalStats, announce, viewingWorkOrder, editingWorkOrder, assigningWorkOrder]);

  const handleCreateSuccess = useCallback((created) => {
    loadWorkOrders(1, true);
    loadGlobalStats();
    announce('created', created && typeof created === 'object' ? created : null); // --- UPDATE
  }, [loadWorkOrders, loadGlobalStats, announce]);

  const handleOptimizeRoutes = useCallback(async () => {
    if (!window.confirm('Run the AI Route Optimizer? This will automatically group nearby pending tickets and assign them to the best available technicians.')) return;
    
    setIsOptimizingRoutes(true);
    try {
        await WorkOrderService.optimizeGeographicRoutes(5); 
        toast({
            title: 'Routes Optimized 🗺️',
            description: 'The AI has successfully batched and assigned nearby work orders.',
            status: 'success',
            duration: 5000,
            isClosable: true
        });
        loadWorkOrders(currentPage);
        loadGlobalStats();
    } catch (err) {
        toast({
            title: 'Optimization Failed',
            description: err.message,
            status: 'error',
            duration: 5000,
            isClosable: true
        });
    } finally {
        setIsOptimizingRoutes(false);
    }
  }, [toast, loadWorkOrders, currentPage, loadGlobalStats]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setSourceFilter('all');
    setSuggestionFilter('all');
    setSlaFilter('all');
    setUnassignedCriticalFilter(false);
    // --- UPDATE
    setStagnantFilter(false);
    setNoAssetFilter(false);
    setNoNotesFilter(false);
    setTypeFilter('all');
    setDomainFilter('all');
    // --- END UPDATE
  }, []);

  // ============================================================================
  // KANBAN DRAG & DROP HANDLERS
  // ============================================================================
  
  const handleDragStart = useCallback((e, workOrderId) => {
    e.dataTransfer.setData('workOrderId', String(workOrderId)); // --- UPDATE: always a string
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault(); 
  }, []);

  const handleDrop = useCallback(async (e, targetStatus) => {
    e.preventDefault();
    const workOrderId = e.dataTransfer.getData('workOrderId');
    if (!workOrderId) return;

    // --- UPDATE: dataTransfer is always a string — compare as strings (numeric ids never matched)
    const order = workOrders.find(wo => String(wo.id) === String(workOrderId));
    if (!order || order.status === targetStatus) return;
    if (targetStatus === 'assigned' && !order.assigned_user_id) {
        setAssigningWorkOrder(order);
        onAssignModalOpen();
        toast({ title: 'Choose a technician', description: 'Assign someone to move this ticket to Assigned.', status: 'info', duration: 3000, isClosable: true });
        return;
    }
    // --- END UPDATE

    if (targetStatus === 'completed') {
        setViewingWorkOrder(order);
        onViewModalOpen();
        toast({
            title: "Resolution Notes Required",
            description: "Please use the status dropdown here to enter resolution notes before completing.",
            status: "info",
            duration: 5000,
            isClosable: true
        });
        return;
    }

    // --- UPDATE: optimistic move, rollback on failure
    const previousStatus = order.status;
    setWorkOrders(prev => prev.map(wo => (wo === order ? { ...wo, status: targetStatus } : wo)));
    try {
      await WorkOrderService.updateWorkOrderStatus(order.id, targetStatus);
      announce('status', order, { status: targetStatus });
      toast({
        title: 'Status Updated',
        description: `Work order moved to ${targetStatus.replace(/_/g, ' ').toUpperCase()}`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
      loadWorkOrders(currentPage, true);
      loadGlobalStats();
    } catch (err) {
      setWorkOrders(prev => prev.map(wo => (String(wo.id) === String(order.id) ? { ...wo, status: previousStatus } : wo))); // --- UPDATE: rollback
      toast({
        title: 'Update Failed',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    }
  }, [workOrders, onViewModalOpen, onAssignModalOpen, toast, loadWorkOrders, currentPage, loadGlobalStats, announce]);

  // ============================================================================
  // AI INSIGHT ACTIONS
  // ============================================================================
  
  const handleInsightAction = useCallback(async (insight) => {
    if (!insight.actionType) return;

    switch (insight.actionType) {
      case 'filter':
        if (insight.actionPayload.filter === 'overdue') {
            setStatusFilter('all');
            setPriorityFilter('all');
            setSearchQuery('');
            setSlaFilter('breached'); 
            setSortField('due_date');
            setSortDirection('asc');
        } else if (insight.actionPayload.filter === 'high-priority') {
            setPriorityFilter('critical'); 
            setStatusFilter('all');        
            setSearchQuery('');
            setSortField('priority'); 
            setSortDirection('desc');
        // --- UPDATE: other filter payloads were ignored
        } else if (insight.actionPayload.filter === 'stagnant') {
            clearFilters();
            setStagnantFilter(true);
            setSort({ field: 'updated_at', dir: 'asc' });
        } else if (insight.actionPayload.filter === 'ai_prediction') {
            clearFilters();
            setSourceFilter('ai_prescriptive');
        } else if (ALLOWED_STATUS.includes(insight.actionPayload.filter)) {
            clearFilters();
            setStatusFilter(insight.actionPayload.filter);
        // --- END UPDATE
        // --- UPDATE (AI Insights engine payloads)
        } else if (insight.actionPayload.filter === 'sla_risk') {
            clearFilters();
            setSlaFilter('at_risk');
            setSort({ field: 'ai_urgency', dir: 'desc' });
        } else if (insight.actionPayload.filter === 'status' && insight.actionPayload.value) {
            clearFilters();
            setStatusFilter(insight.actionPayload.value);
        } else if (insight.actionPayload.filter === 'duration_anomalies' && Array.isArray(insight.actionPayload.ids) && insight.actionPayload.ids.length) {
            openWorkOrderById(insight.actionPayload.ids[0], { fallbackSearch: false });
        // --- END UPDATE
        }
        break;

      // --- UPDATE: stalled tickets ('optimize'), navigation and asset links had no handler
      case 'optimize':
        clearFilters();
        setStagnantFilter(true);
        setSort({ field: 'updated_at', dir: 'asc' });
        break;

      case 'navigate':
        if (insight.actionPayload?.route) navigate(insight.actionPayload.route);
        break;

      case 'open-asset':
        if (insight.actionPayload?.assetId != null) navigate(FLOWSENSE_ROUTES.asset(insight.actionPayload.assetId)); // --- UPDATE: route that exists
        break;
      // --- END UPDATE

      case 'bulk-assign':
        const unassignedWithSuggestions = workOrders.filter(wo => 
            !wo.assigned_user_id && wo.suggested_user_id && 
            wo.status !== 'completed' && wo.status !== 'cancelled' &&
            // --- UPDATE: honour the 'ai_prediction' scope of the predictive-maintenance insight
            (insight.actionPayload?.filter !== 'ai_prediction' || ['ai_prescriptive', 'prediction'].includes(wo.source))
        );

        if (unassignedWithSuggestions.length === 0) {
            toast({ title: 'No Suggestions Available', status: 'warning', duration: 4000 });
            return;
        }

        if (!window.confirm(`Auto-assign ${unassignedWithSuggestions.length} work orders based on AI recommendations?`)) return;

        setIsProcessingAI(true);
        setIsBulkAccepting(true); // --- UPDATE
        try {
            const results = await Promise.all(unassignedWithSuggestions.map(wo => acceptSuggestion(wo.id)));
            results.filter(r => r.status === 'success').forEach(r => announce('assigned', r.id)); // --- UPDATE
            const successCount = results.filter(r => r.status === 'success').length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                toast({
                    title: 'Auto-Assignment Complete',
                    description: `Assigned ${successCount} technicians based on AI predictions.${failCount > 0 ? ` (${failCount} failed)` : ''}`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                });
                loadWorkOrders(currentPage);
                loadGlobalStats();
            } else {
                throw new Error('All assignments failed. Check console for details.');
            }
        } catch (error) {
            toast({ title: 'Assignment Failed', description: error.message, status: 'error', duration: 5000 });
        } finally {
            setIsProcessingAI(false);
            setIsBulkAccepting(false); // --- UPDATE
        }
        break;

      case 'balance':
        setStatusFilter('all');
        setPriorityFilter('all');
        setSearchQuery('');
        setSortField('assigned_user_username');
        setSortDirection('asc');
        break;

      case 'view-backlog':
        setStatusFilter('pending');
        setPriorityFilter('all');
        setSearchQuery('');
        setSortField('created_at');
        setSortDirection('asc');
        break;

      default:
        // --- UPDATE: unknown action types are reported instead of silently ignored
        toast({ title: insight.action || 'Action', description: 'This insight has no automatic action yet.', status: 'info', duration: 3000 });
        break;
    }
  }, [workOrders, toast, acceptSuggestion, loadWorkOrders, currentPage, loadGlobalStats, clearFilters, navigate, announce, setSortField, setSortDirection, openWorkOrderById]); // --- UPDATE: + openWorkOrderById

  // --- UPDATE: AI dispatch assistant bar + View-modal actions
  const handleAiBarAction = useCallback((intent, payload = {}) => {
    switch (intent) {
      case 'FILTER':
        clearFilters();
        if (payload.filter === 'breached' || payload.filter === 'at_risk') setSlaFilter(payload.filter);
        if (payload.filter === 'unassigned_critical') setUnassignedCriticalFilter(true);
        if (payload.filter === 'stagnant') setStagnantFilter(true);
        if (payload.filter === 'no_asset') setNoAssetFilter(true);
        if (payload.filter === 'no_notes') setNoNotesFilter(true);
        setSort({ field: 'ai_urgency', dir: 'desc' });
        break;
      case 'ACCEPT_ALL':
        handleInsightAction({ actionType: 'bulk-assign', actionPayload: {} });
        break;
      case 'VIEW':
        if (payload.workOrder) { setViewingWorkOrder(payload.workOrder); onViewModalOpen(); }
        break;
      default:
        break;
    }
  }, [clearFilters, handleInsightAction, onViewModalOpen]);

  const handleOpenLink = useCallback((link) => {
    // --- UPDATE: a linked work order (parent / inspection / follow-up) opens right here — navigating to
    // this same page with ?search= did nothing because the page was already mounted
    if (link?.kind === 'work_order' && link.id !== undefined && link.id !== null) { openWorkOrderById(link.id, { fallbackSearch: false }); return; }
    // --- END UPDATE
    if (link?.href) { onViewModalClose(); navigate(link.href); }
  }, [navigate, onViewModalClose, openWorkOrderById]);

  // Stable watchdog callbacks (inline arrows made the panel re-subscribe its WebSocket every render)
  const handleWatchdogSync = useCallback(() => {
    loadWorkOrdersRef.current(currentPageRef.current, true);
    loadGlobalStatsRef.current();
  }, []);
  const handleWatchdogFilter = useCallback((type, value) => {
    clearFilters();
    if (type === 'sla' || type === 'sla_status') setSlaFilter(value);
    if (type === 'status') setStatusFilter(value);
    if (type === 'suggestion') setSuggestionFilter(value);
    if (type === 'unassigned_critical') setUnassignedCriticalFilter(true);
    if (type === 'stagnant') setStagnantFilter(true);
    setSort({ field: 'ai_urgency', dir: 'desc' });
  }, [clearFilters]);
  // --- END UPDATE

  const handleExportInsights = useCallback(() => {
    exportAIInsights();
    toast({ title: 'Insights Exported', status: 'success', duration: 3000 });
  }, [exportAIInsights, toast]);

  // --- UPDATE: real CSV export of the current (filtered + sorted) view — was a toast only
  const handleExport = useCallback(() => {
    if (!displayedWorkOrders.length) {
      toast({ title: 'Nothing to export', status: 'warning', duration: 2500 });
      return;
    }
    downloadCsv([WO_CSV_HEADERS, ...displayedWorkOrders.map(woCsvRow)], `work-orders-page${currentPage}-${new Date().toISOString().slice(0, 10)}.csv`);
    toast({ title: 'Export Complete', description: `${displayedWorkOrders.length} work orders exported.`, status: 'success', duration: 3000 });
  }, [displayedWorkOrders, currentPage, toast]);

  // --- UPDATE: pure toggle — the old version set the direction inside the field updater, which
  //   StrictMode runs twice (asc → desc → asc = nothing happened)
  const handleSort = useCallback((field) => {
    setSort(prev => (prev.field === field
      ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: DESC_FIRST_FIELDS.has(field) ? 'desc' : 'asc' }));
  }, []);

  const handleSelectRow = useCallback((orderId) => {
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(orderId)) newSelected.delete(orderId);
      else newSelected.add(orderId);
      setSelectAll(newSelected.size === displayedWorkOrders.length && newSelected.size > 0);
      return newSelected;
    });
  }, [displayedWorkOrders.length]);

  const handleSelectAll = useCallback(() => {
    setSelectAll(prev => {
      if (prev) {
        setSelectedRows(new Set());
        return false;
      }
      setSelectedRows(new Set(displayedWorkOrders.map(wo => wo.id)));
      return true;
    });
  }, [displayedWorkOrders]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} work orders?`)) return;
    
    try {
      await Promise.all(Array.from(selectedRows).map(id => WorkOrderService.deleteWorkOrder(id)));
      Array.from(selectedRows).forEach(id => announce('deleted', id)); // --- UPDATE
      toast({ title: 'Bulk Delete Complete', status: 'success', duration: 3000 });
      setSelectedRows(new Set());
      setSelectAll(false);
      loadWorkOrders(currentPage);
      loadGlobalStats();
    } catch (err) {
      toast({ title: 'Bulk Delete Failed', description: err.message, status: 'error', duration: 5000 });
    }
  }, [selectedRows, toast, loadWorkOrders, currentPage, loadGlobalStats, announce]);

  const handleBulkExport = useCallback(() => {
    const selectedOrders = displayedWorkOrders.filter(wo => selectedRows.has(wo.id));
    // --- UPDATE: quoted CSV (titles with commas broke the file) + AI / asset columns
    downloadCsv([WO_CSV_HEADERS, ...selectedOrders.map(woCsvRow)], `work-orders-selected-${Date.now()}.csv`);
    toast({ title: 'Export Complete', status: 'success', duration: 3000 });
  }, [displayedWorkOrders, selectedRows, toast]);

  const handleNotFoundClear = useCallback(() => {
    setSearchQuery('');
    onNotFoundClose();
  }, [onNotFoundClose]);

  // ============================================================================
  // RBAC CHECKS
  // ============================================================================
  
  const { isAdmin, isStaff, canCreate, canEdit, canDelete, canAssign } = useMemo(() => {
    const _isAdmin = currentUser?.role === 'admin';
    const _isStaff = currentUser?.role === 'staff';
    return {
      isAdmin: _isAdmin,
      isStaff: _isStaff,
      canCreate: _isAdmin || _isStaff,
      canEdit: _isAdmin || _isStaff,
      canDelete: _isAdmin,
      canAssign: _isAdmin || _isStaff,
    };
  }, [currentUser]);

  // --- UPDATE: open the held create request once permissions are known
  useEffect(() => {
    if (!canCreate || pendingCreateRef.current === undefined) return;
    const draft = pendingCreateRef.current;
    pendingCreateRef.current = undefined;
    openCreateWithDraft(draft);
  }, [canCreate, openCreateWithDraft]);
  // --- END UPDATE

  // ============================================================================
  // LiveIndicator component
  // ============================================================================
  const LiveIndicator = () => {
    const config = {
      connected: {
        dotColor: 'green.400',
        bg: wsConnectedBg,
        border: wsConnectedBorder,
        textColor: 'green.600',
        label: 'Live',
        icon: MdSignalWifi4Bar,
        iconColor: 'green.500',
        animate: true,
      },
      connecting: {
        dotColor: 'yellow.400',
        bg: wsConnectingBg,
        border: wsConnectingBorder,
        textColor: 'yellow.600',
        label: 'Connecting',
        icon: MdWifi,
        iconColor: 'yellow.500',
        animate: false,
      },
      reconnecting: {
        dotColor: 'orange.400',
        bg: wsConnectingBg,
        border: wsConnectingBorder,
        textColor: 'orange.600',
        label: 'Reconnecting',
        icon: MdWifi,
        iconColor: 'orange.500',
        animate: false,
      },
      disconnected: {
        dotColor: 'red.400',
        bg: wsDisconnectedBg,
        border: wsDisconnectedBorder,
        textColor: 'red.600',
        label: 'Disconnected',
        icon: MdWifiOff,
        iconColor: 'red.500',
        animate: false,
      },
    }[wsStatus] || {};

    const tooltipLabel = wsStatus === 'connected'
      ? `Live updates active${lastWsEvent ? ` · Last event: ${lastWsEvent.toLocaleTimeString()}` : ''}${wsEventCount > 0 ? ` · ${wsEventCount} update${wsEventCount !== 1 ? 's' : ''} received` : ''}`
      : wsStatus === 'connecting' ? 'Establishing WebSocket connection...'
      : wsStatus === 'reconnecting' ? 'Connection lost — attempting to reconnect...'
      : 'WebSocket disconnected. Data may be stale. Refresh to retry.';

    return (
      <Tooltip label={tooltipLabel} hasArrow placement="bottom">
        <HStack
          spacing={2}
          px={3}
          py={1.5}
          bg={config.bg}
          borderWidth="1px"
          borderColor={config.border}
          borderRadius="full"
          cursor="default"
          userSelect="none"
          transition="all 0.3s"
        >
          <Box position="relative" w="8px" h="8px" flexShrink={0}>
            {config.animate && (
              <Box
                position="absolute"
                top="0" left="0"
                w="8px" h="8px"
                borderRadius="full"
                bg={config.dotColor}
                animation={`${pulseRing} 1.8s ease-out infinite`}
              />
            )}
            <Box
              position="absolute"
              top="0" left="0"
              w="8px" h="8px"
              borderRadius="full"
              bg={config.dotColor}
              animation={config.animate ? `${pulseDot} 1.8s ease-in-out infinite` : undefined}
            />
          </Box>

          <Icon as={config.icon} boxSize={3.5} color={config.iconColor} />

          {!isMobile && (
            <Text fontSize="xs" fontWeight="600" color={config.textColor} letterSpacing="wide">
              {config.label}
            </Text>
          )}

          {wsStatus === 'connected' && wsEventCount > 0 && !isMobile && (
            <Badge
              colorScheme="green"
              fontSize="2xs"
              borderRadius="full"
              px={1.5}
              variant="solid"
            >
              {wsEventCount}
            </Badge>
          )}

          {(wsStatus === 'connecting' || wsStatus === 'reconnecting') && (
            <Spinner size="xs" color={config.iconColor} thickness="2px" />
          )}
        </HStack>
      </Tooltip>
    );
  };

  // ============================================================================
  // COMPONENT HELPERS
  // ============================================================================

  const paginationText = totalItems > 0
    ? `Showing ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems}`
    : `Showing ${displayedWorkOrders.length} items directly`;

  // --- UPDATE: SortableHeader now lives at module level (stable identity, no remount per render)
  const sortProps = { sortField, sortDirection, onSort: handleSort, color: tableHeaderColor, hoverBg: sortableHeaderHoverBg };
  // --- END UPDATE

  const StatCard = ({ title, value, icon, colorScheme = 'blue', trend = null }) => {
    const iconColorMap = { blue: 'blue.500', yellow: 'yellow.500', purple: 'purple.500', green: 'green.500' };
    
    return (
      <ScaleFade in={!isLoading || hasLoadedOnce} initialScale={0.9}>
        <Box bg={cardBg} p={{ base: 4, md: 5 }} borderRadius="xl" boxShadow="md" borderWidth="1px" borderColor={borderColor} borderLeftWidth="4px" borderLeftColor={`${colorScheme}.500`} transition="all 0.2s" _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}>
          <Flex align="center" justify="space-between">
            <Box flex="1">
              <Text fontSize="xs" fontWeight="600" color={mutedColor} mb={1} textTransform="uppercase">{title}</Text>
              <Text as="div" fontSize="2xl" fontWeight="700" color={headingColor}>{isLoading && !hasLoadedOnce ? <Skeleton height="32px" width="50px" /> : value}</Text>
              {trend && <Text fontSize="xs" color={mutedColor} mt={1}>{trend}</Text>}
            </Box>
            <Flex bg={iconBgMap[colorScheme] || blueIconBg} p={3} borderRadius="lg" alignItems="center" justifyContent="center">
              <Icon as={icon} w={6} h={6} color={iconColorMap[colorScheme] || 'blue.500'} />
            </Flex>
          </Flex>
        </Box>
      </ScaleFade>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <Box bg={bgColor} minH="100vh" pt={{ base: "130px", md: "80px", xl: "80px" }} pb={8}>
      <Box maxW="1600px" mx="auto" px={{ base: 4, md: 6, lg: 8 }}>
        
        {/* Header */}
        <Box bg={headerBg} borderRadius="xl" boxShadow="xl" p={6} mb={6} borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
          <Box position="absolute" top="0" left="0" right="0" height="4px" bgGradient="linear(to-r, blue.400, purple.400, pink.400)" opacity="0.8" />
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Box p={2} bg={iconBg} borderRadius="lg"><Icon as={MdAssignment} boxSize={8} color="blue.500" /></Box>
              <VStack align="flex-start" spacing={0}>
                <Heading as="h1" size="lg" color={headingColor}>Work Orders Management</Heading>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  <Badge colorScheme="blue" fontSize="xs">{workOrderStats.total} Total</Badge>
                  {workOrderStats.ai_suggested > 0 && <Badge colorScheme="purple" fontSize="xs"><Icon as={MdAutoAwesome} mr={1} boxSize={2.5} />{workOrderStats.ai_suggested} AI Suggested</Badge>}
                  {workOrderStats.overdue > 0 && <Badge colorScheme="red" fontSize="xs">{workOrderStats.overdue} Overdue</Badge>}
                  <Tooltip label={`Last updated: ${lastRefresh.toLocaleString()}`} hasArrow><Badge colorScheme="green" fontSize="xs" cursor="help">{Math.floor((new Date() - lastRefresh) / 60000)}m ago</Badge></Tooltip>
                  <LiveIndicator />
                </HStack>
              </VStack>
            </HStack>
            
            <HStack spacing={2}>
              <Tooltip label={viewMode === 'table' ? "Switch to Kanban View" : "Switch to Table View"} hasArrow>
                <IconButton icon={<Icon as={viewMode === 'table' ? MdViewColumn : MdList} />} variant="outline" colorScheme="blue" size="sm" onClick={() => setViewMode(viewMode === 'table' ? 'kanban' : 'table')} aria-label="Toggle view" />
              </Tooltip>
              <Tooltip label="Export to CSV" hasArrow>
                <IconButton icon={<Icon as={MdDownload} />} variant="ghost" size="sm" onClick={handleExport} isDisabled={workOrders.length === 0} aria-label="Export" />
              </Tooltip>
              <Tooltip label="Settings" hasArrow>
                <IconButton icon={<Icon as={MdSettings} />} variant="ghost" size="sm" onClick={onSettingsOpen} aria-label="Settings" />
              </Tooltip>
              <Tooltip label="Refresh" hasArrow>
                <IconButton icon={<Icon as={MdRefresh} />} onClick={handleRefresh} isLoading={isLoading} variant="ghost" size="sm" aria-label="Refresh" />
              </Tooltip>
              {canAssign && (
                <Tooltip label="Group nearby tickets and auto-assign" hasArrow>
                  <Button 
                    leftIcon={<Icon as={MdOutlineMap} />} 
                    colorScheme="purple" 
                    variant="solid"
                    onClick={handleOptimizeRoutes}
                    isLoading={isOptimizingRoutes}
                    size="sm"
                    boxShadow="md"
                  >
                    {!isMobile && "Optimize Routes"}
                  </Button>
                </Tooltip>
              )}
              {canCreate && <Button leftIcon={<Icon as={MdAdd} />} colorScheme="blue" onClick={handleCreate} size="sm">Create New</Button>}
              {/* --- UPDATE */}
              {canCreate && <Button variant="outline" colorScheme="cyan" onClick={handleCreateInspection} size="sm" aria-label="New inspection">New Inspection</Button>}
              {/* --- END UPDATE */}
            </HStack>
          </Flex>
        </Box>

        {/* Stats Grid */}
        <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={6}>
          <StatCard title="Total Orders" value={workOrderStats.total} icon={MdList} colorScheme="blue" trend={`${workOrderStats.unassigned} unassigned`} />
          <StatCard title="Pending" value={workOrderStats.pending} icon={MdHourglassEmpty} colorScheme="yellow" trend="Awaiting action" />
          <StatCard title="In Progress" value={workOrderStats.in_progress} icon={MdSync} colorScheme="purple" trend="Active tasks" />
          <StatCard title="Completed" value={workOrderStats.completed} icon={MdDone} colorScheme="green" trend={workOrderStats.total > 0 ? `${((workOrderStats.completed / workOrderStats.total) * 100).toFixed(0)}% rate` : '0% rate'} />
          <StatCard title="AI Predicted" value={workOrderStats.ai_predicted} icon={MdSmartToy} colorScheme="purple" trend="ML Engine" />
          <StatCard title="AI Suggestions" value={workOrderStats.ai_suggested} icon={MdAutoAwesome} colorScheme="purple" trend={workOrderStats.avg_confidence > 0 ? `${workOrderStats.avg_confidence}% avg confidence` : 'No suggestions'} />
        </SimpleGrid>

        <WorkOrderWatchdogPanel 
            workOrders={workOrders}
            onSyncComplete={handleWatchdogSync}
            onApplyFilter={handleWatchdogFilter}
        />

        {/* --- UPDATE: AI dispatch assistant */}
        <Box mt={4} mb={4}>
          <WorkOrderAiActionBar workOrders={workOrders} onAction={handleAiBarAction} isBusy={isBulkAccepting} />
        </Box>
        {/* --- END UPDATE */}

        {wsStatus === 'disconnected' && (
          <Fade in={true}>
            <Alert status="warning" variant="left-accent" borderRadius="xl" mb={4} boxShadow="md">
              <AlertIcon as={MdWifiOff} />
              <Box flex="1">
                <AlertTitle fontSize="sm">Live Updates Disconnected</AlertTitle>
                <AlertDescription fontSize="xs">The real-time connection has been lost. Data shown may be stale. Refresh the page or click Retry to reconnect.</AlertDescription>
              </Box>
              <Button size="sm" colorScheme="orange" variant="solid" onClick={handleRefresh} ml={2}>Retry</Button>
            </Alert>
          </Fade>
        )}

        {/* AI Insights Card */}
        {showInsights && aiInsights.length > 0 && (
          <Fade in={true}>
            <Box bg={insightsBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={insightsBorder} mb={6} boxShadow="md">
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Icon as={MdAutoAwesome} boxSize={5} color="blue.500" />
                  <Heading size="sm" color={headingColor}>AI-Powered Insights & Recommendations</Heading>
                  {isGeneratingInsights && <Spinner size="sm" color="blue.500" />}
                </HStack>
                <HStack spacing={1}>
                  <Tooltip label="Refresh insights" hasArrow><IconButton icon={<Icon as={MdRefresh} />} size="xs" variant="ghost" onClick={handleRefreshInsights} isLoading={isGeneratingInsights} aria-label="Refresh insights" /></Tooltip>
                  <IconButton icon={<Text fontSize="xs">✕</Text>} size="xs" variant="ghost" onClick={() => setShowInsights(false)} aria-label="Close insights" />
                </HStack>
              </HStack>

              {insightsError && <Alert status="error" fontSize="xs" borderRadius="md" mb={3}><AlertIcon />{insightsError}</Alert>}

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {aiInsights.map((insight, index) => {
                  const iconColorMap = { warning: 'orange.500', info: 'blue.500', success: 'green.500' };

                  return (
                    <Box key={index} p={4} bg={insightBgMap[insight.type]} borderRadius="lg" borderWidth="1px" borderColor={insightBorderMap[insight.type]} _hover={{ transform: 'translateY(-2px)', boxShadow: 'md', transition: 'all 0.2s' }} transition="all 0.2s">
                      <HStack align="flex-start" spacing={3}>
                        <Icon as={insight.icon} boxSize={5} color={iconColorMap[insight.type]} mt={0.5} />
                        <VStack align="flex-start" spacing={1} flex="1">
                          <Text fontWeight="bold" fontSize="sm" color={headingColor}>{insight.title}</Text>
                          <Text fontSize="xs" color={textColor}>{insight.message}</Text>
                          {insight.action && (
                            <Button size="xs" variant="ghost" colorScheme="blue" mt={1} onClick={() => handleInsightAction(insight)}>{insight.action}</Button>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Box>
          </Fade>
        )}

        {/* Filters Section */}
        <Box bg={cardBg} p={5} borderRadius="xl" mb={6} borderWidth="1px" borderColor={borderColor} boxShadow="md">
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={2}><Icon as={MdFilterList} color="blue.500" boxSize={5} /><Heading size="sm" color={headingColor}>Filters & Search</Heading></HStack>
            <Button size="sm" variant="ghost" onClick={() => setShowFilters(!showFilters)} rightIcon={showFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}>{showFilters ? 'Hide' : 'Show'}</Button>
          </Flex>

          <Collapse in={showFilters} animateOpacity>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={3}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color={mutedColor} /></InputLeftElement>
                  <Input placeholder="Search work orders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }} />
                </InputGroup>
                
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }}>
                  <option value="all">All Statuses</option>
                  {ALLOWED_STATUS.map(status => <option key={status} value={status}>{status.replace(/_/g, ' ').toUpperCase()}</option>)}
                </Select>
                
                <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }}>
                  <option value="all">All Priorities</option>
                  {ALLOWED_PRIORITY.map(priority => <option key={priority} value={priority}>{priority.charAt(0).toUpperCase() + priority.slice(1)}</option>)}
                </Select>
                
                <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }}>
                  <option value="all">All Sources</option>
                  <option value="ai_prescriptive">🤖 AI Predicted</option>
                  <option value="ai_sensor_alert">📡 Sensor Alert</option>
                  <option value="helpdesk">🎧 Helpdesk</option>
                  <option value="manual">📝 Manual</option>
                </Select>
                
                <Select value={slaFilter} onChange={(e) => setSlaFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }}>
                  <option value="all">All SLAs</option>
                  <option value="compliant">🟢 Compliant</option>
                  <option value="at_risk">🟠 At Risk</option>
                  <option value="breached">🔴 Breached</option>
                </Select>

                <Select 
                  value={suggestionFilter} 
                  onChange={(e) => {
                    setSuggestionFilter(e.target.value);
                    setUnassignedCriticalFilter(false); 
                  }} 
                  bg={inputBg} 
                  borderColor={borderColor} 
                  _hover={{ borderColor: 'blue.400' }}
                >
                  <option value="all">All Assignments</option>
                  <option value="unassigned">Unassigned (All)</option>
                  <option value="assigned">Assigned</option>
                  <option value="has_suggestion">✨ Unassigned w/ AI Suggestion</option>
                  <option value="no_suggestion">No AI Suggestion</option>
                </Select>

                {/* --- UPDATE: type + domain */}
                <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }} aria-label="Filter by type">
                  <option value="all">All Types</option>
                  {TYPE_KEYS.map(k => <option key={k} value={k}>{WORK_ORDER_TYPES[k].label}</option>)}
                </Select>
                <Select value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }} aria-label="Filter by domain">
                  <option value="all">All Domains</option>
                  {DOMAIN_KEYS.map(k => <option key={k} value={k}>{WORK_ORDER_DOMAINS[k].label}</option>)}
                </Select>
                {/* --- END UPDATE */}
              </SimpleGrid>

              {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || sourceFilter !== 'all' || slaFilter !== 'all' || suggestionFilter !== 'all' || unassignedCriticalFilter || stagnantFilter || noAssetFilter || noNotesFilter || typeFilter !== 'all' || domainFilter !== 'all') && (
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" color={mutedColor}>Active filters:</Text>
                  {searchQuery && <Tag size="sm" colorScheme="blue" borderRadius="full">Search: {searchQuery}</Tag>}
                  {statusFilter !== 'all' && <Tag size="sm" colorScheme="purple" borderRadius="full">Status: {statusFilter}</Tag>}
                  {priorityFilter !== 'all' && <Tag size="sm" colorScheme="orange" borderRadius="full">Priority: {priorityFilter}</Tag>}
                  {sourceFilter !== 'all' && <Tag size="sm" colorScheme="purple" borderRadius="full">Source: {sourceFilter}</Tag>}
                  {slaFilter !== 'all' && <Tag size="sm" colorScheme={slaFilter === 'breached' ? 'red' : 'green'} borderRadius="full">SLA: {slaFilter}</Tag>}
                  
                  {suggestionFilter !== 'all' && (
                    <Tag size="sm" colorScheme="purple" borderRadius="full">
                      <Icon as={MdAutoAwesome} mr={1} boxSize={3} />
                      {suggestionFilter === 'has_suggestion' ? 'Has AI Suggestion' : 
                       suggestionFilter === 'no_suggestion' ? 'No Suggestion' : 
                       suggestionFilter === 'unassigned' ? 'Unassigned (All)' : 'Assigned'}
                    </Tag>
                  )}
                  {unassignedCriticalFilter && (
                    <Tag size="sm" colorScheme="red" borderRadius="full">
                      <Icon as={MdPersonOff} mr={1} boxSize={3} />Unassigned Critical
                    </Tag>
                  )}
                  {/* --- UPDATE */}
                  {stagnantFilter && <Tag size="sm" colorScheme="yellow" borderRadius="full">Stagnant 24h+</Tag>}
                  {noAssetFilter && <Tag size="sm" colorScheme="teal" borderRadius="full">Not linked to an asset</Tag>}
                  {noNotesFilter && <Tag size="sm" colorScheme="gray" borderRadius="full">Closed without notes</Tag>}
                  {/* --- UPDATE */}
                  {typeFilter !== 'all' && <Tag size="sm" colorScheme={WORK_ORDER_TYPES[typeFilter]?.color || 'gray'} borderRadius="full">Type: {WORK_ORDER_TYPES[typeFilter]?.short}</Tag>}
                  {domainFilter !== 'all' && <Tag size="sm" colorScheme={WORK_ORDER_DOMAINS[domainFilter]?.color || 'gray'} borderRadius="full">Domain: {WORK_ORDER_DOMAINS[domainFilter]?.short}</Tag>}
                  {/* --- END UPDATE */}
                  {/* --- END UPDATE */}

                  <Button size="xs" variant="ghost" onClick={clearFilters}>Clear All</Button>
                </HStack>
              )}
            </VStack>
          </Collapse>
        </Box>

        {/* Loading State */}
        {/* --- UPDATE: full-page loader only on first load; afterwards a thin bar over the live table */}
        {isLoading && hasLoadedOnce && !error && (
          <Progress size="xs" isIndeterminate colorScheme="blue" borderRadius="full" mb={2} aria-label="Refreshing work orders" />
        )}
        {/* --- END UPDATE */}
        {isLoading && !hasLoadedOnce && (
          <Flex justify="center" align="center" minH="300px">
            <VStack spacing={3}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text color={mutedColor}>Loading work orders...</Text>
              <Progress size="xs" isIndeterminate w="200px" colorScheme="blue" />
            </VStack>
          </Flex>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Alert status="error" variant="left-accent" borderRadius="xl" mb={4} boxShadow="md">
            <AlertIcon />
            <Box flex="1"><AlertTitle>Error</AlertTitle><AlertDescription>{error}</AlertDescription></Box>
            <Button size="sm" onClick={handleRefresh} ml={2}>Retry</Button>
          </Alert>
        )}

        {/* Data Views: TABLE or KANBAN */}
        {(!isLoading || hasLoadedOnce) && !error && ( // --- UPDATE: stay mounted during background reloads
          <Fade in={true}>
            {viewMode === 'table' ? (
              <Box bg={cardBg} borderRadius="xl" boxShadow="md" borderWidth="1px" borderColor={borderColor} overflow="hidden">
                <Flex justify="space-between" align="center" px={4} py={3} borderBottomWidth="1px" borderColor={borderColor} bg={theadBg} flexWrap="wrap" gap={3}>
                  <HStack spacing={3}>
                    <Icon as={MdList} color="blue.500" boxSize={5} />
                    <VStack align="flex-start" spacing={0}>
                      <Text fontWeight="bold" fontSize="sm" color={headingColor}>Work Orders Table</Text>
                      <Text fontSize="xs" color={mutedColor}>
                        {displayedWorkOrders.length} items • {selectedRows.size} selected
                        {workOrderStats.ai_suggested > 0 && <> • <Text as="span" color="purple.500">{workOrderStats.ai_suggested} AI suggested</Text></>}
                        {wsStatus === 'connected' && wsEventCount > 0 && <> • <Text as="span" color="green.500">{wsEventCount} live update{wsEventCount !== 1 ? 's' : ''}</Text></>}
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={2}>
                    <Tooltip label="Advanced details" hasArrow>
                      <IconButton icon={<Icon as={MdTimeline} />} size="sm" variant="ghost" colorScheme={showAdvancedDetails ? 'blue' : 'gray'} onClick={() => setShowAdvancedDetails(!showAdvancedDetails)} aria-label="Advanced details" />
                    </Tooltip>
                  </HStack>
                </Flex>

                {selectedRows.size > 0 && (
                  <Fade in={true}>
                    <Flex bg={bulkActionsBg} px={4} py={2} align="center" justify="space-between" borderBottomWidth="1px" borderColor={borderColor}>
                      <HStack spacing={2}>
                        <Icon as={MdCheckCircle} color="blue.500" />
                        <Text fontSize="sm" fontWeight="medium" color={headingColor}>{selectedRows.size} selected</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Button size="sm" leftIcon={<Icon as={MdDownload} />} onClick={handleBulkExport} variant="ghost" colorScheme="blue">Export Selected</Button>
                        {canDelete && <Button size="sm" leftIcon={<Icon as={MdDelete} />} onClick={handleBulkDelete} variant="ghost" colorScheme="red">Delete Selected</Button>}
                        <Button size="sm" onClick={() => { setSelectedRows(new Set()); setSelectAll(false); }} variant="ghost">Clear Selection</Button>
                      </HStack>
                    </Flex>
                  </Fade>
                )}

                <TableContainer>
                  <Table variant="simple" size={compactView ? 'sm' : tableSize}>
                    <Thead bg={theadBg}>
                      <Tr>
                        <Th width="40px" px={2}><input type="checkbox" checked={selectAll} onChange={handleSelectAll} style={{ cursor: 'pointer' }} /></Th>
                        <SortableHeader {...sortProps} field="id" width="80px">ID</SortableHeader>
                        <Th color={tableHeaderColor} fontSize="xs" textTransform="uppercase" width="100px">Source<WoHint metric="source" /></Th>
                        <SortableHeader {...sortProps} field="title">Title</SortableHeader>
                        <SortableHeader {...sortProps} field="status">Status</SortableHeader>
                        <SortableHeader {...sortProps} field="priority">Priority</SortableHeader>
                        <SortableHeader {...sortProps} field="sla_status" width="100px" hint={<WoHint metric="sla" />}>SLA</SortableHeader>
                        {/* --- UPDATE: AI urgency (client-side triage score) */}
                        <SortableHeader {...sortProps} field="ai_urgency" width="110px" hint={<WoHint metric="urgency" />}>AI Urgency</SortableHeader>
                        <SortableHeader {...sortProps} field="due_date" width="120px">Due Date</SortableHeader>
                        {!isMobile && <SortableHeader {...sortProps} field="assigned_user_username">Assigned / Suggested</SortableHeader>}
                        {showAdvancedDetails && !isMobile && (
                          <><Th color={tableHeaderColor} fontSize="xs" textTransform="uppercase">Created</Th><Th color={tableHeaderColor} fontSize="xs" textTransform="uppercase">Updated</Th></>
                        )}
                        <Th color={tableHeaderColor} fontSize="xs" textTransform="uppercase" isNumeric width="140px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {displayedWorkOrders.length === 0 ? (
                        <Tr>
                          <Td colSpan={(showAdvancedDetails && !isMobile ? 12 : (isMobile ? 8 : 10)) + 1} textAlign="center" py={10}>
                            <VStack spacing={3}>
                              <Icon as={MdList} boxSize={12} color={mutedColor} />
                              <Text color={mutedColor} fontWeight="medium">No work orders found</Text>
                              {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || sourceFilter !== 'all' || suggestionFilter !== 'all' || slaFilter !== 'all' || unassignedCriticalFilter || stagnantFilter || noAssetFilter || noNotesFilter || typeFilter !== 'all' || domainFilter !== 'all') && (
                                <Button size="sm" variant="ghost" onClick={clearFilters} colorScheme="blue">Clear Filters</Button>
                              )}
                            </VStack>
                          </Td>
                        </Tr>
                      ) : (
                        displayedWorkOrders.map((order) => {
                          const isSelected = selectedRows.has(order.id);
                          const isOverdue = order.due_date && new Date(order.due_date).getTime() < nowTime && order.status !== 'completed';
                          const hasPendingSuggestion = !order.assigned_user_id && order.suggested_user_id;
                          const confidence = order.assignment_confidence_score || 0;
                          
                          return (
                            <Tr key={order.id} _hover={{ bg: trHoverBg }} bg={isSelected ? selectedRowBg : (hasPendingSuggestion ? aiSuggestionBg : 'transparent')} borderLeftWidth={isOverdue ? '3px' : (hasPendingSuggestion ? '3px' : '0')} borderLeftColor={isOverdue ? 'red.500' : (hasPendingSuggestion ? 'purple.500' : 'transparent')} transition="all 0.2s">
                              <Td px={2}><input type="checkbox" checked={isSelected} onChange={() => handleSelectRow(order.id)} style={{ cursor: 'pointer' }} /></Td>
                              <Td maxW="120px" px={2}>
                                <Tooltip label={order.id} hasArrow>
                                  <HStack spacing={1}>
                                    {isOverdue && <Icon as={MdWarning} boxSize={3} color="red.500" flexShrink={0} />}
                                    {hasPendingSuggestion && <Icon as={MdAutoAwesome} boxSize={3} color="purple.500" flexShrink={0} />}
                                    <Text fontSize="xs" fontFamily="mono" color={textColor} noOfLines={1}>{String(order.id ?? '').substring(0, 8)}</Text>
                                  </HStack>
                                </Tooltip>
                              </Td>
                              <Td px={2}><SourceBadge source={order.source} /></Td>
                              <Td minW="150px" maxW="350px" px={3}>
                                <VStack align="flex-start" spacing={0}>
                                  <Tooltip label={order.title} hasArrow placement="top">
                                    <Text fontWeight="medium" color={textColor} fontSize={{ base: 'xs', md: 'sm' }} noOfLines={1} w="100%">{order.title}</Text>
                                  </Tooltip>
                                  {showAdvancedDetails && order.description && (
                                    <Text fontSize="xs" color={mutedColor} noOfLines={1} w="100%">{order.description}</Text>
                                  )}
                                  <WoTypeBadge workOrder={order} />{/* --- UPDATE: type · domain */}
                                  {/* --- UPDATE: linked FlowSense asset / road */}
                                  {(() => {
                                    const link = getLinkedEntities(order)[0];
                                    return link ? (
                                      <Text as="button" type="button" fontSize="2xs" color="teal.500" fontWeight="600" noOfLines={1}
                                        onClick={(e) => { e.stopPropagation(); handleOpenLink(link); }} aria-label={`Open ${link.label}`}>
                                        ↗ {link.label}
                                      </Text>
                                    ) : null;
                                  })()}
                                  {/* --- END UPDATE */}
                                </VStack>
                              </Td>
                              <Td px={2} minW="100px">
                                <Tag size="sm" colorScheme={getWorkOrderStatusColorScheme(order.status)} borderRadius="full" fontWeight="semibold" fontSize="xs" maxW="100%">
                                  <Text noOfLines={1}>{String(order.status || 'unknown').replace(/_/g, ' ')}</Text>
                                </Tag>
                              </Td>
                              <Td px={2} minW="90px">
                                <HStack spacing={1}>
                                  {(order.priority === 'high' || order.priority === 'critical') && <Icon as={MdPriorityHigh} boxSize={3} color="red.500" flexShrink={0} />}
                                  <PriorityBadge priority={order.priority} />
                                </HStack>
                              </Td>
                              
                              <Td px={2}>
                                <SLAStatusBadge status={order.sla_status} />
                              </Td>
                              {/* --- UPDATE */}
                              <Td px={2}><WoUrgencyBadge workOrder={order} /></Td>

                              <Td px={2} minW="120px">
                                <VStack align="flex-start" spacing={0}>
                                  <HStack spacing={1}>
                                    <Icon as={MdCalendarToday} boxSize={3} color={mutedColor} flexShrink={0} />
                                    <Text fontSize="xs" color={isOverdue ? 'red.500' : textColor} fontWeight={isOverdue ? 'bold' : 'normal'} noOfLines={1}>{formatDate(order.due_date)}</Text>
                                  </HStack>
                                  {isOverdue && <Badge colorScheme="red" fontSize="2xs" mt={1}>Overdue</Badge>}
                                </VStack>
                              </Td>
                              {!isMobile && (
                                <Td px={2} minW="180px">
                                  {order.assigned_user_username ? (
                                    <HStack spacing={1}><Icon as={MdPersonAdd} boxSize={3} color="green.500" /><Text fontSize="xs">{order.assigned_user_username}</Text></HStack>
                                  ) : hasPendingSuggestion ? (
                                    <VStack align="flex-start" spacing={0}>
                                      <HStack><Icon as={MdAutoAwesome} boxSize={3} color="purple.500" /><Text fontSize="xs" fontWeight="bold" color="purple.600">{order.suggested_technician_name || 'AI Suggestion'}</Text></HStack>
                                      <Badge colorScheme={confidence > 80 ? 'green' : confidence > 60 ? 'yellow' : 'orange'} fontSize="2xs" variant="outline">{confidence}% Match</Badge>
                                    </VStack>
                                  ) : (
                                    <Badge colorScheme="gray" fontSize="2xs">Unassigned</Badge>
                                  )}
                                </Td>
                              )}
                              {showAdvancedDetails && !isMobile && (
                                <>
                                  <Td px={2} minW="100px"><Text fontSize="xs" color={mutedColor} noOfLines={1}>{order.created_at ? formatDate(order.created_at) : 'N/A'}</Text></Td>
                                  <Td px={2} minW="100px"><Text fontSize="xs" color={mutedColor} noOfLines={1}>{order.updated_at ? formatDate(order.updated_at) : 'N/A'}</Text></Td>
                                </>
                              )}
                              <Td isNumeric px={2} minW="140px">
                                <HStack spacing={0} justify="flex-end">
                                  {hasPendingSuggestion && canAssign && (
                                    <Tooltip label={`Accept AI Suggestion (${confidence}%)`} hasArrow>
                                      <IconButton icon={<Icon as={MdThumbUp} />} variant="ghost" size="sm" colorScheme="purple" isLoading={isAcceptingSuggestion === order.id} onClick={(e) => handleAcceptSuggestion(e, order)} aria-label="Accept AI Suggestion" />
                                    </Tooltip>
                                  )}
                                  <Tooltip label="View Details" hasArrow>
                                    <IconButton icon={<Icon as={MdVisibility} />} variant="ghost" size="sm" colorScheme="blue" onClick={() => handleView(order)} aria-label="View" />
                                  </Tooltip>
                                  {!isMobile && canAssign && (
                                    <Tooltip label="Assign" hasArrow><IconButton icon={<Icon as={MdPersonAdd} />} variant="ghost" size="sm" colorScheme="cyan" onClick={() => handleAssign(order)} aria-label="Assign" /></Tooltip>
                                  )}
                                  {!isMobile && canEdit && (
                                    <Tooltip label="Edit" hasArrow><IconButton icon={<Icon as={MdEdit} />} variant="ghost" size="sm" colorScheme="yellow" onClick={() => handleEdit(order)} aria-label="Edit" /></Tooltip>
                                  )}
                                  {!isMobile && canDelete && (
                                    <Tooltip label="Delete" hasArrow><IconButton icon={<Icon as={MdDelete} />} variant="ghost" size="sm" colorScheme="red" onClick={() => handleDelete(order.id)} aria-label="Delete" /></Tooltip>
                                  )}
                                </HStack>
                              </Td>
                            </Tr>
                          );
                        })
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>

                <Flex justify="space-between" align="center" px={4} py={3} borderTopWidth="1px" borderColor={borderColor} direction={{ base: 'column', md: 'row' }} gap={3}>
                  <Text fontSize="sm" color={mutedColor} fontWeight="medium">{paginationText}</Text>
                  <HStack spacing={2}>
                    <Tooltip label="First Page" hasArrow><IconButton icon={<Icon as={MdFirstPage} />} onClick={() => handlePageChange(1)} isDisabled={currentPage <= 1 || isLoading} size="sm" variant="ghost" aria-label="First Page" /></Tooltip>
                    <Tooltip label="Previous Page" hasArrow><IconButton icon={<Icon as={MdNavigateBefore} />} onClick={() => handlePageChange(currentPage - 1)} isDisabled={currentPage <= 1 || isLoading} size="sm" variant="ghost" aria-label="Previous" /></Tooltip>
                    <HStack><Text fontSize="sm" color={textColor}>Page</Text><NumberInput size="sm" maxW="70px" min={1} max={totalPages} value={currentPage} onChange={(_, val) => handlePageChange(val)}><NumberInputField /><NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper></NumberInput><Text fontSize="sm" color={textColor}>of {totalPages}</Text></HStack>
                    <Tooltip label="Next Page" hasArrow><IconButton icon={<Icon as={MdNavigateNext} />} onClick={() => handlePageChange(currentPage + 1)} isDisabled={currentPage >= totalPages || isLoading} size="sm" variant="ghost" aria-label="Next" /></Tooltip>
                    <Tooltip label="Last Page" hasArrow><IconButton icon={<Icon as={MdLastPage} />} onClick={() => handlePageChange(totalPages)} isDisabled={currentPage >= totalPages || isLoading} size="sm" variant="ghost" aria-label="Last Page" /></Tooltip>
                  </HStack>
                </Flex>
              </Box>
            ) : (
              /* KANBAN BOARD VIEW */
              <Flex gap={4} overflowX="auto" pb={4} align="stretch" minH="65vh" css={{ '&::-webkit-scrollbar': { height: '8px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: borderColor, borderRadius: '4px' } }}>
                {KANBAN_COLUMNS.map(column => {
                  const columnOrders = displayedWorkOrders.filter(wo => column.statuses.includes(wo.status));

                  return (
                    <Box
                      key={column.id}
                      flex="1"
                      minW="320px"
                      bg={kanbanColumnBg}
                      borderRadius="xl"
                      p={4}
                      borderWidth="1px"
                      borderColor={borderColor}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, column.targetStatus)}
                    >
                      <HStack justify="space-between" mb={4} borderBottomWidth="2px" borderColor={`${column.color}.400`} pb={2}>
                        <HStack>
                          <Icon as={column.icon} color={`${column.color}.500`} boxSize={5} />
                          <Heading size="sm" textTransform="uppercase">{column.label}</Heading>
                        </HStack>
                        <Badge colorScheme={column.color} borderRadius="full" px={2}>{columnOrders.length}</Badge>
                      </HStack>

                      <VStack spacing={3} align="stretch">
                        {columnOrders.map(wo => {
                           const hasPendingSuggestion = !wo.assigned_user_id && wo.suggested_user_id;
                           return (
                              <Box
                                key={wo.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, wo.id)}
                                onDragEnd={handleDragEnd}
                                bg={cardBg}
                                p={4}
                                borderRadius="lg"
                                boxShadow="sm"
                                borderWidth="1px"
                                borderColor={hasPendingSuggestion ? 'purple.300' : borderColor}
                                borderLeftWidth="4px"
                                borderLeftColor={
                                  wo.priority === 'critical' ? 'red.500' :
                                  wo.priority === 'high' ? 'orange.500' : 'blue.500'
                                }
                                cursor="grab"
                                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                                transition="all 0.2s"
                                onClick={() => handleView(wo)}
                              >
                                <Flex justify="space-between" align="start" mb={2}>
                                  <HStack spacing={2}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500" fontFamily="mono">{String(wo.id ?? '').substring(0, 8)}</Text>
                                    <WoUrgencyBadge workOrder={wo} />{/* --- UPDATE */}
                                    <WoTypeBadge workOrder={wo} showDomain={false} />{/* --- UPDATE */}
                                  </HStack>
                                  <HStack>
                                      <SLAStatusBadge status={wo.sla_status} />
                                      <SourceBadge source={wo.source} />
                                  </HStack>
                                </Flex>
                                
                                <Text fontWeight="bold" fontSize="sm" mb={2} noOfLines={2}>{wo.title}</Text>
                                
                                <HStack justify="space-between" mt={4}>
                                  <Badge colorScheme={wo.priority === 'critical' ? 'red' : wo.priority === 'high' ? 'orange' : 'gray'}>
                                    {wo.priority}
                                  </Badge>
                                  
                                  {hasPendingSuggestion ? (
                                    <Tooltip label={`AI Suggests: ${wo.suggested_technician_name}`}>
                                        <Badge colorScheme="purple" variant="outline" fontSize="2xs"><Icon as={MdAutoAwesome} mr={1}/>{wo.assignment_confidence_score}%</Badge>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip label={wo.assigned_user_username ? `Assigned to ${wo.assigned_user_username}` : 'Unassigned'}>
                                        <Avatar size="xs" name={wo.assigned_user_username} bg={wo.assigned_user_id ? 'blue.500' : 'gray.300'} icon={!wo.assigned_user_id ? <MdAssignment fontSize="1rem" /> : undefined} />
                                    </Tooltip>
                                  )}
                                </HStack>
                              </Box>
                           );
                        })}
                        
                        {columnOrders.length === 0 && (
                          <Flex justify="center" align="center" h="100px" borderStyle="dashed" borderWidth="2px" borderColor={borderColor} borderRadius="lg">
                            <Text color="gray.400" fontSize="sm">Drop tickets here</Text>
                          </Flex>
                        )}
                      </VStack>
                    </Box>
                  );
                })}
              </Flex>
            )}
          </Fade>
        )}

        {/* Settings Modal */}
        <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="md">
          <ModalOverlay />
          <ModalContent bg={cardBg}>
            <ModalHeader color={headingColor}>Display Settings</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                <FormControl display="flex" alignItems="center" justify="space-between">
                  <FormLabel mb="0" color={textColor}>Compact View</FormLabel>
                  <Switch colorScheme="blue" isChecked={compactView} onChange={(e) => setCompactView(e.target.checked)} />
                </FormControl>
                <FormControl display="flex" alignItems="center" justify="space-between">
                  <FormLabel mb="0" color={textColor}>Show AI Insights</FormLabel>
                  <Switch colorScheme="blue" isChecked={showInsights} onChange={(e) => setShowInsights(e.target.checked)} />
                </FormControl>
                <FormControl display="flex" alignItems="center" justify="space-between">
                  <FormLabel mb="0" color={textColor}>Show Filters</FormLabel>
                  <Switch colorScheme="blue" isChecked={showFilters} onChange={(e) => setShowFilters(e.target.checked)} />
                </FormControl>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2} color={headingColor} fontSize="sm">AI Insights</Text>
                  <VStack align="stretch" spacing={2} fontSize="sm">
                    <HStack justify="space-between"><Text color={textColor}>Total insights:</Text><Code fontSize="xs">{aiInsightsData.length}</Code></HStack>
                    <HStack justify="space-between"><Text color={textColor}>Last generated:</Text><Code fontSize="xs">{insightsLastGenerated ? insightsLastGenerated.toLocaleTimeString() : 'Never'}</Code></HStack>
                    <HStack justify="space-between"><Text color={textColor}>AI Suggestions:</Text><Code fontSize="xs" colorScheme="purple">{workOrderStats.ai_suggested}</Code></HStack>
                  </VStack>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2} color={headingColor} fontSize="sm">Live Connection</Text>
                  <VStack align="stretch" spacing={2} fontSize="sm">
                    <HStack justify="space-between">
                      <Text color={textColor}>Status:</Text>
                      <HStack spacing={1}>
                        <Box w="7px" h="7px" borderRadius="full" bg={wsStatus === 'connected' ? 'green.400' : wsStatus === 'disconnected' ? 'red.400' : 'yellow.400'} />
                        <Code fontSize="xs" colorScheme={wsStatus === 'connected' ? 'green' : wsStatus === 'disconnected' ? 'red' : 'yellow'}>{wsStatus}</Code>
                      </HStack>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={textColor}>Last event:</Text>
                      <Code fontSize="xs">{lastWsEvent ? lastWsEvent.toLocaleTimeString() : 'None yet'}</Code>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={textColor}>Events this session:</Text>
                      <Code fontSize="xs" colorScheme="green">{wsEventCount}</Code>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Action Modals */}
        <RecordNotFoundModal isOpen={isNotFoundOpen} onClose={onNotFoundClose} query={notFoundQuery} entityName="Work Order" onClear={handleNotFoundClear} />
        <CreateWorkOrderForm isOpen={isCreateModalOpen} onClose={onCreateModalClose} onSuccess={handleCreateSuccess} prefill={createDraft} />{/* --- UPDATE: prefill */}
        <EditWorkOrderForm isOpen={isEditModalOpen} onClose={onEditModalClose} onSuccess={handleSuccess} initialData={editingWorkOrder} />
        <AssignWorkOrderModal isOpen={isAssignModalOpen} onClose={onAssignModalClose} onSuccess={handleSuccess} workOrder={assigningWorkOrder} />
        <ViewWorkOrderModal isOpen={isViewModalOpen} onClose={onViewModalClose} workOrder={viewingWorkOrder} onUpdate={handleSuccess}
          // --- UPDATE: AI helper actions route back to the page
          onAssign={canAssign ? (wo) => { onViewModalClose(); handleAssign(wo); } : undefined}
          onEdit={canEdit ? (wo) => { onViewModalClose(); handleEdit(wo); } : undefined}
          onOpenLink={handleOpenLink}
          onCreateLinked={canCreate ? handleCreateLinked : undefined}
        />
      </Box>
    </Box>
  );
}

export default WorkOrderPage;